import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, assetUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { BrandMark } from "@/components/BrandMark";
import {
  Loader2, Copy, ChevronDown, ExternalLink, Sparkles, ArrowLeft,
  LogIn, Lock, Share2, Store, MapPin, Phone,
} from "lucide-react";
import { toast } from "sonner";

const ANTON = '"Anton", "Impact", sans-serif';

/**
 * Static-Offer landing page (`/offers/:id`).
 *
 * **Login gate** — Static Offer has no lead form fallback, so this
 * page is a hard gate: logged-out visitors can preview the offer but
 * cannot see the coupon code until they sign in. This matches the
 * accountability model that Spin/Scratch get via their lead form.
 *
 * **Auto-claim on return** — When the login-gate sends the user to
 * `/customer/login?next=/offers/{id}?autoclaim=1`, the login page
 * bounces them back here with `autoclaim=1` on the URL; we detect
 * that + a fresh token and fire the claim automatically so it feels
 * like a single tap.
 *
 * **Share** — Reuses the same `navigator.share` + copy-fallback
 * pattern used by the win popup. Share URL is `/api/share/o/{id}`
 * (backend), which serves OG meta tags to social crawlers and
 * redirects real browsers back to this page — so WhatsApp / iMessage
 * / etc. render the campaign's Featured Image + title + T&Cs in the
 * link preview card without any image-generation infrastructure.
 */
export default function OfferPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [offer, setOffer] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [claim, setClaim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);
  const isLoggedIn = Boolean(localStorage.getItem("citydooh_customer_token"));
  // Guard against React 18 strict-mode double-fire on the auto-claim
  // path — we only ever want ONE POST /offers/{id}/claim per return trip.
  const autoclaimFiredRef = useRef(false);

  useEffect(() => {
    api.publicOfferDetail(id)
      .then((r) => setOffer(r.data))
      .catch((e) => {
        // Same defensive coercion as PlayPage — a bare API error
        // object would otherwise crash React with 'Objects are not
        // valid as a React child' when rendered inside <Card>.
        const det = e?.response?.data?.detail;
        const msg =
          typeof det === "string"
            ? det
            : det && typeof det === "object"
            ? det.message || det.code || "Offer not found"
            : "Offer not found";
        setLoadErr(String(msg));
      });
  }, [id]);

  const handleClaim = async () => {
    // Belt-and-braces: if a logged-out user somehow clicks claim
    // (script inject, cached UI, etc.) route them to login instead
    // of hitting the API and swallowing a 401 toast.
    if (!isLoggedIn) {
      goToLogin();
      return;
    }
    setBusy(true);
    try {
      const r = await api.claimOffer(id);
      setClaim(r.data);
      if (r.data?.lead_saved) toast.success("Saved to My Winnings");
    } catch (e) {
      const status = e?.response?.status;
      // If the token expired between page load and click, fall back
      // to the login gate instead of showing a raw "401" toast.
      if (status === 401) {
        localStorage.removeItem("citydooh_customer_token");
        toast.info("Please sign in to claim this offer");
        goToLogin();
        return;
      }
      const d = e?.response?.data?.detail;
      const msg = typeof d === "object" ? d?.message : d;
      toast.error(msg || "Couldn't claim this offer");
    } finally {
      setBusy(false);
    }
  };

  const goToLogin = () => {
    // Round-trip: come back to this exact offer + trigger auto-claim.
    const next = `/offers/${id}?autoclaim=1`;
    navigate(`/customer/login?next=${encodeURIComponent(next)}`);
  };

  // Auto-claim on return from login. Requires:
  //   (1) `autoclaim=1` on the URL (set by goToLogin above)
  //   (2) a valid customer token in localStorage
  //   (3) the offer detail has loaded
  //   (4) we haven't already fired (StrictMode double-mount guard)
  useEffect(() => {
    if (!offer) return;
    if (claim) return;
    if (searchParams.get("autoclaim") !== "1") return;
    if (!isLoggedIn) return;
    if (autoclaimFiredRef.current) return;
    autoclaimFiredRef.current = true;
    // Strip the ?autoclaim param so a refresh doesn't re-fire the
    // POST (would be a duplicate coupon on the second load).
    const params = new URLSearchParams(searchParams);
    params.delete("autoclaim");
    setSearchParams(params, { replace: true });
    handleClaim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer]);

  const copyCode = async () => {
    if (!claim?.coupon_code) return;
    try {
      await navigator.clipboard.writeText(claim.coupon_code);
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const handleShare = async () => {
    // Share URL points at the backend OG endpoint so WhatsApp /
    // iMessage / Slack scrapers can build a rich preview card from
    // the campaign's own thumbnail + title + description. Real
    // browsers hitting the same URL get bounced back to
    // `/offers/{id}` immediately (see routes/share.py).
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const shareUrl = `${origin}/api/share/o/${id}`;
    const biz = offer?.vendor?.business || "a local shop";
    const title = `${offer?.name || "Offer"} — ${biz}`;
    const text = `Check out this offer from ${biz}!`;

    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title, text, url: shareUrl });
        return;
      } catch (err) {
        // AbortError = user cancelled the OS share sheet — treat as a
        // no-op, don't fall through to copy or they'll get an
        // unexpected clipboard toast.
        if (err && (err.name === "AbortError" || err.code === 20)) return;
        // Any other error → fall through to clipboard fallback below.
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedShare(true);
      toast.success("Link copied — paste it anywhere!");
      setTimeout(() => setCopiedShare(false), 2200);
    } catch {
      toast.error("Couldn't copy the share link — try again.");
    }
  };

  if (loadErr) {
    return (
      <OfferShell>
        <Card
          className="p-8 max-w-md mx-auto text-center border-2 border-[#0A0A0A] bg-white text-[#0A0A0A]"
          style={{ boxShadow: "4px 4px 0 #0A0A0A" }}
          data-testid="offer-error"
        >
          <p className="text-2xl uppercase mb-2" style={{ fontFamily: ANTON }}>Offer not available</p>
          <p className="text-sm text-[#0A0A0A]/60">{String(loadErr)}</p>
        </Card>
      </OfferShell>
    );
  }
  if (!offer) {
    return (
      <OfferShell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-[#0A0A0A]/60" />
        </div>
      </OfferShell>
    );
  }

  const { prize, vendor } = offer;
  return (
    <OfferShell>
      <div className="max-w-xl mx-auto">
        <Card
          className="overflow-hidden border-2 border-[#0A0A0A] bg-white text-[#0A0A0A]"
          style={{ boxShadow: "8px 8px 0 #0A0A0A" }}
          data-testid="offer-page-card"
        >
          {offer.thumbnail_url && (
            <img
              src={assetUrl(offer.thumbnail_url)}
              alt=""
              className="w-full aspect-video object-cover border-b-2 border-[#0A0A0A]"
            />
          )}
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              {offer.logo_url ? (
                <img
                  src={assetUrl(offer.logo_url)}
                  alt=""
                  className="h-12 w-12 rounded-lg object-cover border-2 border-[#0A0A0A]"
                />
              ) : (
                <div
                  className="h-12 w-12 rounded-lg bg-[#0A0A0A] text-[#F5A800] flex items-center justify-center text-xl border-2 border-[#0A0A0A]"
                  style={{ fontFamily: ANTON }}
                >
                  {(vendor?.business || "?").slice(0, 1)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate text-[#0A0A0A]">{vendor?.business}</p>
                {vendor?.city && <p className="text-xs text-[#0A0A0A]/60">{vendor.city}</p>}
              </div>
              <Badge
                variant="outline"
                className="text-[10px] font-black uppercase tracking-widest border-2 border-[#0A0A0A] bg-[#F5A800] text-[#0A0A0A]"
              >
                Instant Offer
              </Badge>
            </div>

            <p
              className="text-xs uppercase tracking-[0.25em] text-[#F5A800] font-black mb-1"
              data-testid="offer-page-supertitle"
              style={{ fontFamily: ANTON }}
            >
              Claim Your Offer
            </p>
            <h1
              className="text-3xl md:text-4xl uppercase leading-tight mb-2 text-[#0A0A0A]"
              style={{ fontFamily: ANTON }}
              data-testid="offer-title"
            >
              {prize?.gift_name || prize?.label}
            </h1>
            <div className="flex items-center gap-3 mb-6 text-sm text-[#0A0A0A]/60">
              {offer.expires_at && (
                <span>Ends {new Date(offer.expires_at).toLocaleDateString()}</span>
              )}
              {prize?.remaining !== undefined && (
                <span data-testid="offer-remaining">
                  Only <strong className="text-[#0A0A0A]">{prize.remaining}</strong> left of {prize.total_quantity}
                </span>
              )}
            </div>

            {!claim ? (
              <>
                {isLoggedIn ? (
                  <Button
                    size="lg"
                    onClick={handleClaim}
                    disabled={busy || prize?.remaining === 0}
                    className="w-full h-12 text-base font-black uppercase tracking-widest bg-[#0A0A0A] text-[#F5A800] border-2 border-[#0A0A0A] hover:bg-[#F5A800] hover:text-[#0A0A0A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontFamily: ANTON }}
                    data-testid="offer-claim-button"
                  >
                    {busy ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : prize?.remaining === 0 ? (
                      "Sold out"
                    ) : (
                      <>
                        <Sparkles className="h-5 w-5 mr-2" /> Get This Offer
                      </>
                    )}
                  </Button>
                ) : (
                  // Hard login-gate. The coupon code area is intentionally
                  // NOT rendered here — we cannot even hint at what the
                  // code would look like until the customer signs in.
                  <div
                    className="rounded-xl border-2 border-dashed border-[#0A0A0A]/40 bg-[#F7F4EC] p-5 text-center"
                    data-testid="offer-login-gate"
                  >
                    <div className="mx-auto h-11 w-11 rounded-full bg-[#0A0A0A] text-[#F5A800] flex items-center justify-center mb-3">
                      <Lock className="h-5 w-5" />
                    </div>
                    <p
                      className="text-xl uppercase leading-tight mb-1 text-[#0A0A0A]"
                      style={{ fontFamily: ANTON }}
                      data-testid="offer-login-gate-title"
                    >
                      Sign in to claim this offer
                    </p>
                    <p className="text-sm text-[#0A0A0A]/60 mb-4">
                      A quick email code — no password, no clutter. Your coupon appears the moment you&apos;re in.
                    </p>
                    <Button
                      size="lg"
                      onClick={goToLogin}
                      disabled={prize?.remaining === 0}
                      className="w-full h-12 text-base font-black uppercase tracking-widest bg-[#0A0A0A] text-[#F5A800] border-2 border-[#0A0A0A] hover:bg-[#F5A800] hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
                      style={{ fontFamily: ANTON }}
                      data-testid="offer-signin-cta"
                    >
                      <LogIn className="h-5 w-5 mr-2" />
                      {prize?.remaining === 0 ? "Sold out" : "Sign In to Claim"}
                    </Button>
                  </div>
                )}

                {/* Share is always available — a logged-out visitor can
                    still forward the offer's preview card so friends can
                    see and sign in themselves. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShare}
                  className="w-full mt-3 h-10 text-xs font-bold uppercase tracking-widest border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F5A800] hover:border-[#0A0A0A]"
                  data-testid="offer-share-button"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  {copiedShare ? "Link copied!" : "Share this offer"}
                </Button>
              </>
            ) : (
              <div
                className="rounded-xl border-2 border-[#0A0A0A] bg-[#F7F4EC] p-5 text-center relative"
                data-testid="offer-claim-success"
              >
                <p className="text-sm text-[#0A0A0A]/60 mb-2">Your coupon code:</p>
                <div
                  className="inline-block bg-[#F5A800] text-[#0A0A0A] font-mono font-black text-2xl tracking-widest px-6 py-3 rounded-lg border-2 border-[#0A0A0A]"
                  data-testid="offer-coupon-code"
                >
                  {claim.coupon_code}
                </div>
                <div className="flex items-center justify-center gap-3 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-2 border-[#0A0A0A] font-bold uppercase tracking-widest text-xs"
                    onClick={copyCode}
                  >
                    <Copy className="h-4 w-4 mr-1" /> Copy
                  </Button>
                  {claim.redirect_url && (
                    <a
                      href={claim.redirect_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center bg-[#0A0A0A] text-[#F5A800] font-black uppercase tracking-widest text-xs px-3 py-2 rounded-md hover:bg-[#F5A800] hover:text-[#0A0A0A] transition-colors border-2 border-[#0A0A0A]"
                      style={{ fontFamily: ANTON }}
                    >
                      Redeem now <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                    </a>
                  )}
                </div>
                {claim.expires_at && (
                  <p className="text-xs text-[#0A0A0A]/60 mt-3">
                    Valid until {new Date(claim.expires_at).toLocaleDateString()}
                  </p>
                )}
                {claim.lead_saved ? (
                  <p className="text-[11px] mt-3 text-emerald-700 font-bold">✓ Saved to My Winnings</p>
                ) : null}

                {/* 02/08 — How to redeem block. Business name, address
                    and phone only ever surface here (someone who just
                    won something needs to know where to redeem it).
                    Anonymous browsers on Explore never see any of it. */}
                {(claim.vendor_contact?.business ||
                  claim.vendor_contact?.address ||
                  claim.vendor_contact?.phone) && (
                  <div
                    className="mt-4 pt-4 border-t border-[#0A0A0A]/10 text-left"
                    data-testid="offer-how-to-redeem"
                  >
                    <p
                      className="text-[10px] font-black uppercase tracking-[0.25em] text-[#0A0A0A]/60"
                      style={{ fontFamily: ANTON }}
                    >
                      How to redeem
                    </p>
                    <div className="mt-2 space-y-1.5 text-sm">
                      {claim.vendor_contact.business && (
                        <p className="flex items-start gap-2" data-testid="redeem-business">
                          <Store className="h-4 w-4 mt-0.5 text-[#F5A800] shrink-0" />
                          <span className="font-bold text-[#0A0A0A]">{claim.vendor_contact.business}</span>
                        </p>
                      )}
                      {claim.vendor_contact.address && (
                        <p className="flex items-start gap-2" data-testid="redeem-address">
                          <MapPin className="h-4 w-4 mt-0.5 text-[#F5A800] shrink-0" />
                          <span className="text-[#0A0A0A]/80 whitespace-pre-wrap">
                            {claim.vendor_contact.address}
                            {claim.vendor_contact.city ? `, ${claim.vendor_contact.city}` : ""}
                          </span>
                        </p>
                      )}
                      {claim.vendor_contact.phone && (
                        <p className="flex items-center gap-2" data-testid="redeem-phone">
                          <Phone className="h-4 w-4 text-[#F5A800] shrink-0" />
                          <a
                            href={`tel:${claim.vendor_contact.phone}`}
                            className="text-[#0A0A0A] font-bold underline hover:text-[#F5A800]"
                          >
                            {claim.vendor_contact.phone}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Post-claim share — encourage the customer to send the
                    offer to a friend. Same handler, same OG endpoint. */}
                <div className="mt-4 pt-4 border-t border-[#0A0A0A]/10">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShare}
                    className="h-9 text-xs font-bold uppercase tracking-widest border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F5A800] hover:border-[#0A0A0A]"
                    data-testid="offer-share-after-claim"
                  >
                    <Share2 className="h-3.5 w-3.5 mr-1.5" />
                    {copiedShare ? "Link copied!" : "Share with friends"}
                  </Button>
                </div>
              </div>
            )}

            {(offer.terms_and_conditions || prize?.terms) && (
              <Collapsible className="mt-6">
                <CollapsibleTrigger
                  className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[#0A0A0A]/60 hover:text-[#0A0A0A]"
                  data-testid="offer-terms-toggle"
                >
                  <ChevronDown className="h-4 w-4" /> Terms &amp; Conditions
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 text-xs text-[#0A0A0A]/70 whitespace-pre-wrap leading-relaxed">
                  {prize?.terms || offer.terms_and_conditions}
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        </Card>
      </div>
    </OfferShell>
  );
}

/**
 * Slim brand shell used by every public single-page (Offer, Login, etc.)
 * so the top-of-page chrome never drifts.
 */
function OfferShell({ children }) {
  return (
    <div className="min-h-screen bg-[#F7F4EC] text-[#0A0A0A] flex flex-col">
      <header className="border-b-2 border-[#0A0A0A] bg-[#F7F4EC]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="offer-brand">
            <BrandMark size="header" />
          </Link>
          <Link
            to="/explore"
            className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A] hover:text-[#F5A800] inline-flex items-center gap-1"
            data-testid="offer-back-to-explore"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Explore
          </Link>
        </div>
      </header>
      <main className="flex-1 py-10 px-4">{children}</main>
    </div>
  );
}
